import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";

function RedirectWithParams({ to }: { to: string }) {
  const params = useParams();
  let target = to;
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) target = target.replace(`:${k}`, v);
  }
  return <Navigate to={target} replace />;
}

/** Look up a profile's username by UUID and 301-redirect to the username URL. */
function RedirectProfileByUserId() {
  const { userId } = useParams();
  const [target, setTarget] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) { setTarget("/saljare"); return; }
      const { data } = await (supabase as any)
        .from("public_profiles")
        .select("username")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      setTarget(data?.username ? `/saljare/${data.username}` : "/saljare");
    })();
    return () => { cancelled = true; };
  }, [userId]);
  if (!target) return null;
  return <Navigate to={target} replace />;
}
import { AuthProvider } from "@/contexts/AuthContext";
import { NavigationHistoryProvider } from "@/contexts/NavigationHistoryContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import { SystemAlertBanner } from "./components/SystemAlertBanner";
import { ProfileCompletionGate } from "./components/ProfileCompletionGate";

const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const Tools = lazy(() => import("./pages/Tools"));
const About = lazy(() => import("./pages/About"));
const Terms = lazy(() => import("./pages/Terms"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateListingLayout = lazy(() => import("./pages/CreateListingLayout"));
const CreateListingStep1 = lazy(() => import("./pages/CreateListingStep1"));
const CreateListingStep2 = lazy(() => import("./pages/CreateListingStep2"));
const CreateListingStep3 = lazy(() => import("./pages/CreateListingStep3"));
const CreateListingStep4 = lazy(() => import("./pages/CreateListingStep4"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const CreateService = lazy(() => import("./pages/CreateService"));
const CreateProviderProfile = lazy(() => import("./pages/CreateProviderProfile"));
const MessageThread = lazy(() => import("./pages/MessageThread"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AlertsTest = lazy(() => import("./pages/AlertsTest"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const AreaCalculatorHub = lazy(() => import("./pages/AreaCalculatorHub"));
const AreaCalculatorMap = lazy(() => import("./pages/AreaCalculatorMap"));
const Members = lazy(() => import("./pages/Members"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminUploadListing = lazy(() => import("./pages/AdminUploadListing"));
const EditListing = lazy(() => import("./pages/EditListing"));

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
        <NavigationHistoryProvider>
          <SystemAlertBanner />
          <ProfileCompletionGate />
          <main id="main-content">
          <Suspense fallback={null}>
          <Routes>
            <Route path="/profil-setup" element={<CompleteProfile />} />
            <Route path="/" element={<Index />} />
            <Route path="/skogstjanster" element={<Services />} />
            <Route path="/skogstjanster/:id" element={<ServiceDetail />} />
            <Route path="/annonser" element={<Marketplace />} />
            <Route path="/annonser/:id" element={<ListingDetail />} />
            <Route path="/saljare" element={<Members />} />
            <Route path="/skogsverktyg" element={<Tools />} />
            <Route path="/skogsverktyg/arearaknare" element={<ProtectedRoute><AreaCalculatorHub /></ProtectedRoute>} />
            <Route path="/skogsverktyg/arearaknare/karta" element={<ProtectedRoute><AreaCalculatorMap /></ProtectedRoute>} />
            <Route path="/skogsverktyg/arearaknare/karta/:areaId" element={<ProtectedRoute><AreaCalculatorMap /></ProtectedRoute>} />
            <Route path="/om-oss" element={<About />} />
            <Route path="/villkor" element={<Terms />} />
            <Route path="/integritetspolicy" element={<PrivacyPolicy />} />
            <Route path="/logga-in" element={<Login />} />
            <Route path="/registrera" element={<Register />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><Admin /></ProtectedRoute>} />
            <Route path="/admin/alerts-test" element={<ProtectedRoute requiredRole="admin"><AlertsTest /></ProtectedRoute>} />
            <Route path="/admin/ladda-upp-annons" element={<ProtectedRoute requiredRole="admin"><AdminUploadListing /></ProtectedRoute>} />
            <Route path="/skapa-annons" element={<ProtectedRoute><CreateListingLayout /></ProtectedRoute>}>
              <Route index element={<CreateListingStep1 />} />
              <Route path="detaljer" element={<CreateListingStep2 />} />
              <Route path="platsinfo" element={<CreateListingStep3 />} />
              <Route path="pris" element={<CreateListingStep4 />} />
            </Route>
            <Route path="/annonser/:id/redigera" element={<ProtectedRoute><EditListing /></ProtectedRoute>} />
            <Route path="/redigera-profil" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
            <Route path="/skapa-tjanst" element={<ProtectedRoute requiredAccountType="company"><CreateService /></ProtectedRoute>} />
            <Route path="/skapa-leverantorsprofil" element={<ProtectedRoute requiredAccountType="company"><CreateProviderProfile /></ProtectedRoute>} />
            <Route path="/meddelanden/:threadId" element={<ProtectedRoute><MessageThread /></ProtectedRoute>} />
            <Route path="/saljare/:username" element={<PublicProfile />} />

            {/* 301 redirects from old paths to new SEO-friendly paths */}
            <Route path="/marknadsplats" element={<Navigate to="/annonser" replace />} />
            <Route path="/marknadsplats/:id" element={<RedirectWithParams to="/annonser/:id" />} />
            <Route path="/marknadsplats/:id/redigera" element={<RedirectWithParams to="/annonser/:id/redigera" />} />
            <Route path="/tjanster" element={<Navigate to="/skogstjanster" replace />} />
            <Route path="/tjanster/:id" element={<RedirectWithParams to="/skogstjanster/:id" />} />
            <Route path="/medlemmar" element={<Navigate to="/saljare" replace />} />
            <Route path="/verktyg" element={<Navigate to="/skogsverktyg" replace />} />
            <Route path="/verktyg/arearaknare" element={<Navigate to="/skogsverktyg/arearaknare" replace />} />
            <Route path="/verktyg/arearaknare/karta" element={<Navigate to="/skogsverktyg/arearaknare/karta" replace />} />
            <Route path="/verktyg/arearaknare/karta/:areaId" element={<RedirectWithParams to="/skogsverktyg/arearaknare/karta/:areaId" />} />
            <Route path="/profil/:userId" element={<RedirectProfileByUserId />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </main>
        </NavigationHistoryProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;
